import { utils } from "@intrasync/utils";

import {
  makeObservable,
  computed,
  action,
  observable,
  runInAction,
  when,
} from "mobx";

import AuthStore from "./AuthStore";
import { RouterStore, startRouter } from "./RouterStore";
import ServiceStore from "./ServiceStore";
import type { ServiceStoreOptions } from "types/ServiceStoreTypes";
import NotificationStore from "./NotificationStore";
import ViewStore from "./ViewStore";
import ChannelStore from "./ChannelStore";
import ConnectionStore from "./ConnectionStore";
import RequestQueueStore from "./RequestQueueStore";
import RegistrationStore from "./RegistrationStore";
import AppLayoutStore, { LayoutOptions } from "./AppLayoutStore";
import WebApiStore from "./WebApiStore";
import BarcodeCameraStore from "./BarcodeCameraStore";
import DataGridStore from "./DataGridStore/DataGridStore";
import ThemeStore from "./ThemeStore";
import FindAllStore from "./findAllStore/FindAllStore";
import apiClient from "../api-client/apiClient";
import { builtInServices, builtInServiceStores } from "./builtInStores";

import type { Application } from "@feathersjs/feathers";
import serviceStores from "../serviceStoresConfig";
import ScheduleGridStore from "./ScheduleGridStore/ScheduleGridStore";
import ConversationStore from "./ConversationStore";
import SettingsStore from "./SettingsStore";
import { RouterStoreProps } from "./RouterStore/RouterStore";
import { Theme } from "@mui/material/styles";
import SignaturePadStore from "./SignaturePadStore";
import { CadStore } from "./CadStore";
import { ServiceStoreManager } from "./ServiceStoreManager";
import { GlobalReactionOptimizer } from "./utils/GlobalReactionOptimizer";
import { MemoryMonitor } from "./utils/MemoryMonitor";
import { StoreRegistry } from "./StoreRegistry";
import { OfflineQueueManager } from "./offline/OfflineQueueManager";
import { PWAStore } from "./PWAStore";
import { BrowserAPIStore } from "./browser/BrowserAPIStore";
import { PerformanceStore } from "./PerformanceStore";
import PdfEditorStore from "./PdfEditorStore";
import ConflictStore from "./ConflictStore";

declare const window: Window;

type Mode =
  | "dev"
  | "development"
  | "stage"
  | "beta"
  | "live"
  | "prod"
  | "production"
  | "test";

export type RootStoreProps = {
  appName: string;
  appTitle?: string;
  storageName?: string;
  storage?: LocalForage | Storage;
  apiOptions?: unknown;
  viewStoreProps?: unknown;
  mode: Mode;
  appVersion?: string;
  autoUpdate?: boolean;
  syncChannels?: boolean;
  serviceStores?: unknown[];
  appStores?: unknown[];
  asyncStorage?: boolean;
  enableRouterStore?: boolean;
  routerStoreProps?: RouterStoreProps;
  enableNotificationStore?: boolean;
  enableAuthStore?: unknown;
  disableLogsStore?: boolean;
  enableRequestQueue?: boolean;
  disableViewStore?: boolean;
  enableWebApiStore?: unknown;
  enableBarcodeCameraStore?: boolean | unknown;
  enableDataGridStore?: boolean;
  enableScheduleGridStore?: boolean;
  enableFindAllStore?: object | boolean;
  dataGridStore?: DataGridStore;
  scheduleGridStore?: ScheduleGridStore;
  app?: Application;
  routerStore?: RouterStore | undefined;
  requestQueueStore?: RequestQueueStore;
  notificationStore?: NotificationStore;
  channelStore?: ChannelStore;
  connectionStore?: ConnectionStore;
  authStore?: AuthStore | undefined;
  viewStore?: ViewStore;
  registrationStore?: RegistrationStore;
  appLayoutStore?: LayoutOptions;
  webApiStore?: WebApiStore;
  barcodeCameraStore?: BarcodeCameraStore;
  findAllStore?: FindAllStore;
  channelStoreDebugMode?: boolean;
  customTheme?: unknown;
};

class RootStore {
  // [x: string]: unknown;
  app;
  debugMode = false;
  setupComplete = false;
  channelServices: string[] = [];
  apiOptions = {
    debugMode: false,
    uriOverride: {
      development: "",
      stage: "",
      production: "",
    },
    enableFindOne: true,
  };

  appName: string;
  appVersion: string;
  autoUpdate: boolean;
  appTitle: string;
  syncChannels: boolean;
  authStorageKey: string;
  refreshStorageKey: string;
  mode: Mode;
  storage: LocalForage | Storage | unknown;
  viewStoreProps: Record<string, unknown>;
  serviceStores: string[] = [];
  appStores: string[] = [];
  storageName: string = "";
  asyncStorage: boolean;
  enableRouterStore: boolean = false;
  enableNotificationStore: boolean = false;
  enableAuthStore: boolean = false;
  disableLogsStore: boolean = false;
  enableRequestQueue: boolean = false;
  disableViewStore: boolean = false;
  enableWebApiStore: boolean = false;
  enableBarcodeCameraStore: boolean = false;
  enableDataGridStore: boolean = false;
  enableScheduleGridStore: boolean = false;
  enableFindAllStore: object | boolean = false;
  dataGridStore: DataGridStore | null = null;
  scheduleGridStore: ScheduleGridStore | null = null;
  routerStore?: RouterStore;
  authStore?: AuthStore;
  viewStore: ViewStore | null = null;
  connectionStore: ConnectionStore | null = null;
  channelStore: ChannelStore | null = null;
  notificationStore: NotificationStore | null = null;
  requestQueueStore: RequestQueueStore | null = null;
  registrationStore: RegistrationStore | null = null;
  appLayoutStore: AppLayoutStore | null = null;
  webApiStore: WebApiStore | null = null;
  barcodeCameraStore: BarcodeCameraStore | null = null;
  findAllStore: FindAllStore | null = null;
  userStore: ServiceStore | null = null;
  authRedirectStore: ServiceStore | null = null;
  themeStore!: ThemeStore;
  cadStore!: CadStore;
  serviceStoreManager: ServiceStoreManager | null = null;
  reactionOptimizer: GlobalReactionOptimizer | null = null;
  memoryMonitor: MemoryMonitor | null = null;
  storeRegistry: StoreRegistry;
  offlineQueueManager: OfflineQueueManager | null = null;
  conflictStore: ConflictStore | null = null;
  pwaStore: PWAStore | null = null;
  browserAPIStore: BrowserAPIStore | null = null;
  performanceStore: PerformanceStore | null = null;
  pdfEditorStore: PdfEditorStore | null = null;
  [key: string]: unknown;

  staticProps = {
    appName: "",
    appVersion: "",
    autoUpdate: false,
    appTitle: "",
    syncChannels: false,
    authStorageKey: "",
    refreshStorageKey: "",
    mode: "",
    storage: "",
    viewStoreProps: {},
    serviceStores: [],
    appStores: [],
    storageName: "",
    asyncStorage: false,
    enableRouterStore: false,
    enableNotificationStore: false,
    enableAuthStore: false,
    disableLogsStore: false,
    enableRequestQueue: false,
    disableViewStore: false,
    enableWebApiStore: false,
    enableBarcodeCameraStore: false,
    enableDataGridStore: false,
    enableScheduleGridStore: false,
    apiOptions: {},
    channelStoreDebugMode: false,
  };
  userPermissionsStore: ServiceStore | null = null;
  logStore: ServiceStore | null = null;

  constructor(props: RootStoreProps) {
    // Initialize store registry first
    this.storeRegistry = new StoreRegistry(this);

    makeObservable(this, {
      setupComplete: observable,
      channelServices: observable,
      mountStore: action,
      dismountStore: action,
      setSetupComplete: action,
      setChannelServices: action,
      anyServiceStoreLoading: computed,
      anyServiceStoreHasError: computed,
      storeCount: computed,
      isConnected: computed,
    });

    this.appName =
      props.appName || props.storageName || `@intrasync/${utils.uuid()}`;
    if (!props.storage && !window.localStorage) {
      throw Error("Needs a storage of some sorts");
    }

    if (
      !props.storage &&
      typeof window !== "undefined" &&
      "localStorage" in window
    ) {
      this.storage = window.localStorage;
    } else {
      const isLocalForage = props.storage && !!props.storage.createInstance;
      if (isLocalForage) {
        if (props.storage) {
          this.storage = props.storage.createInstance({ name: this.appName });
        }
      } else {
        this.storage = props.storage;
      }
    }

    this.mode = props.mode || "dev";
    this.appVersion = props.appVersion || "unknown";
    this.autoUpdate = props.autoUpdate || false;
    this.appTitle = props.appTitle || "IntraSync App";
    this.syncChannels = props.syncChannels || props.enableRouterStore || false;
    this.authStorageKey = `feathers-jwt${
      !["production", "prod", "live"].includes(this.mode) ? `-${this.mode}` : ""
    }`;
    this.refreshStorageKey = `refresh-token${
      !["production", "prod", "live"].includes(this.mode) ? `-${this.mode}` : ""
    }`;

    let allApiOptions = {
      debugMode: true,
      uriOverride: {
        development: "",
        stage: "",
        production: "",
      },
      enableFindOne: true,
      authStorageKey: this.authStorageKey,
      refreshStorageKey: this.refreshStorageKey,
    };
    if (props.apiOptions && typeof props.apiOptions === "object") {
      allApiOptions = { ...allApiOptions, ...(props.apiOptions as object) };
      runInAction(() => {
        this.apiOptions = allApiOptions;
      });
    }

    const app = apiClient(this.mode, allApiOptions);

    const getRootStoreServices = () => {
      return [
        ...builtInServices,
        ...serviceStores.map((service: { name: string }) => service.name),
      ];
    };

    const rootStoreServiceStoreExists = (serviceName: string) => {
      const storeName = utils.servicePathToStoreName(serviceName);
      return this.storeRegistry.hasStore(storeName);
    };

    const services = getRootStoreServices();

    services.forEach((service: string) => {
      const serviceName = service;
      const apiService = app.service(service);
      apiService.on("created", (item: Record<string, unknown>) => {
        if (rootStoreServiceStoreExists(serviceName)) {
          const storeName = utils.servicePathToStoreName(serviceName);
          const serviceStore = this.storeRegistry.getServiceStore(storeName);
          serviceStore?.addOrUpdateEvent(item);
        }
      });
      apiService.on("removed", (item: Record<string, unknown>) => {
        if (rootStoreServiceStoreExists(serviceName)) {
          const storeName = utils.servicePathToStoreName(serviceName);
          const serviceStore = this.storeRegistry.getServiceStore(storeName);
          serviceStore?.removeEvent(item);
        }
      });
      apiService.on("patched", (item: Record<string, unknown>) => {
        if (rootStoreServiceStoreExists(serviceName)) {
          const storeName = utils.servicePathToStoreName(serviceName);
          const serviceStore = this.storeRegistry.getServiceStore(storeName);
          serviceStore?.addOrUpdateEvent(item);
        }
      });
    });

    this.app = app;

    // Initialize OfflineQueueManager for offline support
    // Pass appName to consolidate all offline storage into a single IndexedDB
    this.offlineQueueManager = new OfflineQueueManager(this.appName);

    // Initialize ConflictStore for conflict resolution UI
    this.conflictStore = new ConflictStore(this);

    // Initialize PWA Store for progressive web app features
    this.pwaStore = new PWAStore(this);

    // Initialize Browser API Store
    this.browserAPIStore = new BrowserAPIStore(this);

    // Initialize Performance Store
    this.performanceStore = new PerformanceStore(this);

    // Initialize PDF Editor Store for PDF collaboration
    this.pdfEditorStore = new PdfEditorStore(this);

    this.mountStore("conversationStore", new ConversationStore(this));
    if (props.viewStoreProps && typeof props.viewStoreProps === "object") {
      this.viewStoreProps = { ...(props.viewStoreProps as object) };
    }
    if (props.enableWebApiStore) {
      this.mountStore("webApiStore", new WebApiStore(this));
    }
    if (props.enableBarcodeCameraStore) {
      const barcodeCameraStoreProps =
        typeof props.enableBarcodeCameraStore === "object"
          ? props.enableBarcodeCameraStore
          : {};
      this.mountStore(
        "barcodeCameraStore",
        new BarcodeCameraStore(this, barcodeCameraStoreProps)
      );
    }

    this.mountStore(
      "themeStore",
      new ThemeStore(this, props.customTheme as Theme | undefined)
    );
    this.mountStore("settingsStore", new SettingsStore(this));
    // ProductStore and ProductViewStore are now configured in appStoresConfig.ts
    this.mountStore("cadStore", new CadStore(this));

    if (props.enableDataGridStore) {
      this.setupDataGridStore();
    }
    if (props.enableScheduleGridStore) {
      this.setupScheduleGridStore();
    }
    if (props.enableFindAllStore) {
      const findAllStoreProps =
        typeof props.enableFindAllStore === "object"
          ? props.enableFindAllStore
          : {};
      this.findAllStore = new FindAllStore(this, findAllStoreProps);
    }
    if (!props.disableLogsStore) {
      this.mountStore(
        "logStore",
        new ServiceStore(this, "log", {
          enableStorage: false,
          enableErrorNotifications: true,
        })
      );
    }
    if (props.enableRequestQueue) {
      this.mountStore("requestQueueStore", new RequestQueueStore(this, {}));
    }
    if (props.enableNotificationStore) {
      this.mountStore("notificationStore", new NotificationStore(this));
    }
    // Initialize ServiceStoreManager for batched store loading
    if (props.serviceStores && props.serviceStores.length > 0) {
      // Initializing ServiceStoreManager
      this.serviceStores = (props.serviceStores as { name: string }[]).map(
        (service: { name: string }) => `${utils.uriToCamel(service.name)}Store`
      );

      // Create ServiceStoreManager with smaller batch size for performance
      this.serviceStoreManager = new ServiceStoreManager(
        this,
        props.serviceStores as { name: string; options: ServiceStoreOptions }[],
        100 // Reduced from 15 to 20 for faster initial batches
      );

      // Load more batches immediately to ensure critical stores are available
      // Only initialize first 2 batches (16 stores) immediately for faster startup
      this.serviceStoreManager
        .initializeEagerBatches(2)
        .catch((error: Error) => {
          console.error(
            "Failed to initialize eager service store batch:",
            error
          );
        });
    }

    // Mount critical stores first (auth-related)
    this.mountStore(
      "refreshTokenStore",
      new ServiceStore(this, "refresh-token", {
        enableStorage: false,
        enableErrorNotifications: true,
      })
    );
    this.mountStore(
      "userStore",
      new ServiceStore(this, "user", {
        enableStorage: true,
        enableErrorNotifications: true,
      })
    );
    this.mountStore(
      "userPermissionsStore",
      new ServiceStore(this, "user-permissions", {
        enableStorage: true,
        enableErrorNotifications: true,
      })
    ).then(() => {
      this.userPermissionsStore?.handleFirstLoad();
    });

    // Defer non-critical stores until after auth setup
    this.deferredStoreInitialization();

    this.mountStore(
      "authStore",
      new AuthStore(this, {
        debugMode:
          (props.enableAuthStore as { debugMode: boolean }).debugMode || false,
        authStorageKey: this.authStorageKey,
        refreshStorageKey: this.refreshStorageKey,
        idField: "id",
        userService: "user",
        entityField: "user",
      })
    );

    if (this.syncChannels) {
      const options = {
        debugMode: props.channelStoreDebugMode ?? false,
      };
      this.mountStore("channelStore", new ChannelStore(this, options));
    }

    if (!("routes" in (props.routerStoreProps ?? {}))) {
      throw Error("Routes List Required");
    }

    if (props.routerStoreProps) {
      this.mountStore(
        "routerStore",
        new RouterStore(
          this,
          Object.values(props.routerStoreProps.routes),
          Object.values(props.routerStoreProps.layouts),
          {
            config: {
              html5history: props.routerStoreProps?.isHashRouter ? false : true,
              defaultRoute: props.routerStoreProps.defaultRoute || "Home",
            },
            appName: this.appName || "Unknown",
            appVersion: props.appVersion || "unknown",
            debugMode: props.routerStoreProps.debugMode || false,
            defaultRoute: props.routerStoreProps.defaultRoute || "Home",
            historyEnabled: true,
          }
        )
      ).then(() => {
        // Set up router initialization after auth is ready
        this.initializeRouterWhenReady(props);
      });
    }

    this.mountStore(
      "appLayoutStore",
      new AppLayoutStore(this, {
        toolboxBase: props?.appLayoutStore?.toolboxBase || "",
        rightNavButtonTitle: props?.appLayoutStore?.rightNavButtonTitle || "",
        rightNavDefaultRoute: props?.appLayoutStore?.rightNavDefaultRoute || "",
      })
    );

    if (props.appStores && props.appStores.length > 0) {
      this.appStores = (props.appStores as { name: string }[]).map(
        (store: { name: string }) => store.name
      );
      props.appStores.forEach(
        (appStore: {
          name: string;
          store: new (rootStore: RootStore) => unknown;
        }) => {
          this.mountStore(
            (
              appStore as {
                name: string;
                store: new (rootStore: RootStore) => unknown;
              }
            ).name,
            new (
              appStore as {
                name: string;
                store: new (rootStore: RootStore) => unknown;
              }
            ).store(this)
          );
        }
      );
    }

    if (!props.disableViewStore) {
      this.mountStore("viewStore", new ViewStore(this));
    }
    if (this.app && this.mode !== "test") {
      this.mountStore("connectionStore", new ConnectionStore(this));
    }

    // Initialize reaction optimization after all stores are created
    this.reactionOptimizer = new GlobalReactionOptimizer(this);
    this.reactionOptimizer.initialize();

    // Optimize reactions for key stores
    this.reactionOptimizer.optimizeAuthReactions();
    this.reactionOptimizer.optimizeConnectionReactions();
    this.reactionOptimizer.optimizeNotificationReactions();

    // Log optimization results
    if (this.debugMode) {
      console.log(this.reactionOptimizer.getOptimizationReport());
    }

    // Initialize memory monitoring
    this.memoryMonitor = MemoryMonitor.getInstance();

    // Expose store count for memory monitoring
    (window as any).__STORE_COUNT__ = this.storeCount;

    // Expose memory monitor globally for debugging
    if (this.mode === "development" || this.debugMode) {
      (window as any).__MEMORY_MONITOR__ = this.memoryMonitor;
    }

    // Start memory monitoring in development mode
    if (this.mode === "development" && this.debugMode) {
      this.memoryMonitor.startMonitoring(60000); // Monitor every 60 seconds (increased from 30)

      // Run automated disposal tests only once
      setTimeout(() => {
        if (this.memoryMonitor && !this.memoryMonitor.hasRunTests) {
          this.memoryMonitor
            .runAutomatedTests(this)
            .then((results: unknown) => {
              console.log("Automated memory tests completed:", results);
              if (this.debugMode) {
                console.log(this.memoryMonitor?.generateReport());
              }
            })
            .catch((error: Error) => {
              console.error("Memory tests failed:", error);
            });
        }
      }, 10000); // Increased delay to 10 seconds
    }

    this.setSetupComplete();
  }

  setChannelServices = (serviceNames: string[]) => {
    this.channelServices = serviceNames;
  };

  setSetupComplete = () => {
    this.setupComplete = true;
    // rootStore is ready
  };

  /**
   * Initialize router when both routes are loaded and auth is ready
   */
  private initializeRouterWhenReady = (props: RootStoreProps) => {
    // Use when() to wait for both conditions
    when(
      // Condition: routes initialized AND auth is not pending
      () => {
        const routesReady = this.routerStore?.routesInitialized === true;
        const authReady = !this.authStore?.isAuthenticatePending;
        const isSignedIn = this.authStore?.isSignedIn === true;

        // Check router initialization conditions

        // Start router if routes are ready AND (auth is ready OR user is not signed in)
        // This handles both authenticated and non-authenticated routes
        return routesReady && authReady;
      },
      // Action: start the router
      () => {
        // Starting router - all conditions met
        startRouter(this, {
          defaultRoute: props.routerStoreProps?.defaultRoute || "Home",
          html5history: props.routerStoreProps?.isHashRouter ? false : true,
        });
      }
    );
  };

  /**
   * Initialize non-critical stores after auth setup to improve startup performance
   */
  private deferredStoreInitialization = () => {
    // Use setTimeout to defer initialization until next tick
    setTimeout(() => {
      this.mountStore("signaturePadStore", new SignaturePadStore(this));
      this.mountStore(
        "appVersionStore",
        new ServiceStore(this, "app-version", {})
      );
      this.mountStore(
        "authRedirectStore",
        new ServiceStore(this, "auth-redirect", {
          enableStorage: false,
          enableErrorNotifications: true,
        })
      );
      this.mountStore(
        "userFeedbackStore",
        new ServiceStore(this, "user-feedback", {
          enableStorage: true,
          enableErrorNotifications: true,
        })
      );
      this.mountStore(
        "emailLoginStore",
        new ServiceStore(this, "email-login", {
          enableStorage: false,
          enableErrorNotifications: true,
        })
      );
      this.mountStore(
        "userSubscriptionStore",
        new ServiceStore(this, "user-subscription", {
          enableStorage: false,
          enableErrorNotifications: true,
        })
      );
      this.mountStore(
        "emailActivateUserStore",
        new ServiceStore(this, "email/activate-user", {
          enableStorage: false,
          enableErrorNotifications: true,
        })
      );
      this.mountStore(
        "emailPasswordResetStore",
        new ServiceStore(this, "email/password-reset", {
          enableStorage: false,
          enableErrorNotifications: true,
        })
      );
      this.mountStore(
        "emailPasswordResetVerificationStore",
        new ServiceStore(this, "email/password-reset-verification", {
          enableStorage: false,
          enableErrorNotifications: true,
        })
      );
      this.mountStore("registrationStore", new RegistrationStore(this));

      // Initialize remaining service store batches
      if (this.serviceStoreManager) {
        this.serviceStoreManager
          .ensureAllBatchesInitialized()
          .catch((error: Error) => {
            console.error(
              "Failed to initialize remaining service store batches:",
              error
            );
          });
      }
    }, 10); // 10ms delay to allow auth initialization to start
  };

  mountStore = (storeName: string, store: unknown, isServiceStore = false) => {
    this.debugMode && console.log("mountStore storeName", storeName);

    let actualStore: unknown;

    if (
      typeof store === "function" &&
      store.prototype &&
      store.prototype.constructor
    ) {
      actualStore = new (store as new (rootStore: RootStore) => unknown)(this);
    } else {
      actualStore = store;
    }

    // Use StoreRegistry for type-safe registration
    if (isServiceStore) {
      this.storeRegistry.registerServiceStore(
        storeName,
        actualStore as ServiceStore,
        (actualStore as ServiceStore).name || storeName
      );
    } else {
      // Determine store type and register appropriately
      const storeType = this.getStoreType(storeName);
      if (storeType === "builtin") {
        this.storeRegistry.registerBuiltinStore(
          storeName,
          actualStore,
          storeType
        );
      } else {
        this.storeRegistry.registerViewStore(storeName, actualStore);
      }
    }

    // Maintain backward compatibility
    this[storeName] = actualStore;

    return Promise.resolve(`${storeName} added to rootStore`);
  };

  /**
   * Helper method to determine store type for registry
   */
  private getStoreType(storeName: string): "builtin" | "view" {
    const builtinStores = [
      "authStore",
      "viewStore",
      "connectionStore",
      "channelStore",
      "notificationStore",
      "requestQueueStore",
      "registrationStore",
      "appLayoutStore",
      "webApiStore",
      "barcodeCameraStore",
      "findAllStore",
      "themeStore",
      "cadStore",
      "dataGridStore",
      "scheduleGridStore",
      "settingsStore",
      "signaturePadStore",
      "appVersionStore",
      "authRedirectStore",
      "userFeedbackStore",
      "emailLoginStore",
      "userPermissionsStore",
      "userSubscriptionStore",
      "emailActivateUserStore",
      "emailPasswordResetStore",
      "emailPasswordResetVerificationStore",
      "refreshTokenStore",
      "userStore",
      "logStore",
    ];

    return builtinStores.includes(storeName) ? "builtin" : "view";
  }

  dismountStore = (storeName: string | number) => {
    const storeNameStr = storeName.toString();

    if (!this.storeRegistry.hasStore(storeNameStr)) {
      return console.warn("that store was not mounted");
    }

    // Use StoreRegistry for safe disposal
    const disposed = this.storeRegistry.unregisterStore(storeNameStr);

    if (!disposed) {
      console.warn(`Failed to dispose store: ${storeNameStr}`);
    }

    return Promise.resolve(`${storeName} removed from rootStore`);
  };

  unmountStore = (storeName: string | number) => {
    return this.dismountStore(storeName);
  };

  setupDataGridStore = (specificName?: string) => {
    try {
      if (specificName) {
        this.mountStore(specificName, new DataGridStore(this));
        return true;
      }
      this.mountStore("dataGridStore", new DataGridStore(this));
      return true;
    } catch (err) {
      return false;
    }
  };

  setupScheduleGridStore = (specificName?: string) => {
    // console.log('setupScheduleGridStore specificName', specificName);
    try {
      if (specificName) {
        this.mountStore(specificName, new ScheduleGridStore(this));
        return true;
      }
      this.mountStore("scheduleGridStore", new ScheduleGridStore(this));
      return true;
    } catch (err) {
      return false;
    }
  };

  disposeAll = () => {
    console.log("RootStore: Beginning comprehensive disposal...");

    // Dispose all stores using StoreRegistry (type-safe)
    try {
      if (this.serviceStoreManager) {
        this.serviceStoreManager.disposeAll();
        this.serviceStoreManager = null;
      }

      // Use StoreRegistry for comprehensive disposal
      this.storeRegistry.disposeAll();
    } catch (error) {
      console.error("Error disposing stores:", error);
    }

    // Dispose all app stores
    try {
      (this.appStores as string[])?.forEach((storeName: string) => {
        const store = this.storeRegistry.getStore(storeName);
        if (
          store &&
          typeof (store as Record<string, unknown>).dispose === "function"
        ) {
          (store as { dispose: () => void }).dispose();
        }
      });
    } catch (error) {
      console.error("Error disposing app stores:", error);
    }

    // Dispose built-in stores with error handling
    try {
      this.authStore?.dispose();
      this.userPermissionsStore?.dispose();
      this.viewStore?.dispose();
      this.connectionStore?.dispose();

      // Safe disposal for stores that may not have dispose method
      if (this.channelStore && "dispose" in this.channelStore) {
        (this.channelStore as { dispose: () => void }).dispose();
      }
      if (this.notificationStore && "dispose" in this.notificationStore) {
        (this.notificationStore as { dispose: () => void }).dispose();
      }
      if (this.requestQueueStore && "dispose" in this.requestQueueStore) {
        (this.requestQueueStore as { dispose: () => void }).dispose();
      }
      if (this.registrationStore && "dispose" in this.registrationStore) {
        (this.registrationStore as { dispose: () => void }).dispose();
      }
      if (this.appLayoutStore && "dispose" in this.appLayoutStore) {
        (this.appLayoutStore as { dispose: () => void }).dispose();
      }
      if (this.webApiStore && "dispose" in this.webApiStore) {
        (this.webApiStore as { dispose: () => void }).dispose();
      }
      if (this.barcodeCameraStore && "dispose" in this.barcodeCameraStore) {
        (this.barcodeCameraStore as { dispose: () => void }).dispose();
      }
      if (this.findAllStore && "dispose" in this.findAllStore) {
        (this.findAllStore as { dispose: () => void }).dispose();
      }
      if (this.themeStore && "dispose" in this.themeStore) {
        (this.themeStore as { dispose: () => void }).dispose();
      }
      if (this.cadStore && "dispose" in this.cadStore) {
        (this.cadStore as { dispose: () => void }).dispose();
      }
      if (this.dataGridStore && "dispose" in this.dataGridStore) {
        (this.dataGridStore as { dispose: () => void }).dispose();
      }
      if (this.scheduleGridStore && "dispose" in this.scheduleGridStore) {
        (this.scheduleGridStore as { dispose: () => void }).dispose();
      }
    } catch (error) {
      console.error("Error disposing built-in stores:", error);
    }

    // Remove all service event listeners to prevent memory leaks (fixes 272+ listener accumulation)
    if (this.app) {
      try {
        const services = [
          ...builtInServices,
          ...serviceStores.map((service: { name: string }) => service.name),
        ];
        services.forEach((serviceName: string) => {
          const service = this.app.service(serviceName);
          if (service?.removeAllListeners) {
            service.removeAllListeners();
          }
        });
      } catch (error) {
        console.error("Error removing service event listeners:", error);
      }
    }

    // Dispose reaction optimizer
    if (this.reactionOptimizer) {
      this.reactionOptimizer.dispose();
      this.reactionOptimizer = null;
    }

    // Stop memory monitoring and generate final report
    if (this.memoryMonitor) {
      if (this.debugMode) {
        console.log(
          "Final Memory Report:",
          this.memoryMonitor.generateReport()
        );
      }
      this.memoryMonitor.stopMonitoring();
      this.memoryMonitor = null;
    }

    // Clear global store count
    if (typeof window !== "undefined") {
      delete (window as any).__STORE_COUNT__;
    }

    console.log("RootStore: Disposal completed");
  };

  get serviceStoreChannels() {
    if (!this.serviceStores?.length) return [];
    return [...this.serviceStores, ...builtInServiceStores].reduce<string[]>(
      (servicesInChannels: string[], serviceStoreName: unknown): string[] => {
        const storeName = serviceStoreName as string;
        // Use StoreRegistry for type-safe access
        const serviceStore = this.storeRegistry.getServiceStore(storeName);
        if (serviceStore?.enableAutoChannel) {
          servicesInChannels.push(storeName);
        }
        const uniqueServicesInChannels = utils.uniqueArray(servicesInChannels);
        return uniqueServicesInChannels as string[];
      },
      []
    );
  }

  get currentChannelsServices() {
    if (!this.serviceStoreChannels?.length) return [];
    return this.serviceStoreChannels
      .filter((serviceStoreName: string) => {
        // Use StoreRegistry for type-safe access
        const serviceStore =
          this.storeRegistry.getServiceStore(serviceStoreName);
        return serviceStore?.name;
      })
      .map((serviceStoreName: string) => {
        const serviceStore =
          this.storeRegistry.getServiceStore(serviceStoreName);
        return serviceStore!.name;
      });
  }

  get anyServiceStoreLoading() {
    if (!this.serviceStores?.length) return false;
    return this.serviceStores.reduce<string[]>(
      (serviceStoreIsLoading, serviceStoreName) => {
        // Use StoreRegistry for type-safe access
        const serviceStore = this.storeRegistry.getServiceStore(
          serviceStoreName as string
        );
        if (serviceStore?.isServicePending) {
          serviceStoreIsLoading = [
            ...serviceStoreIsLoading,
            serviceStoreName as string,
          ];
        }
        return serviceStoreIsLoading;
      },
      []
    );
  }

  get anyServiceStoreHasError() {
    if (!this.serviceStores?.length) return false;
    return this.serviceStores.reduce(
      (serviceStoreHasError: unknown[], serviceStoreName: unknown) => {
        // Use StoreRegistry for type-safe access
        const serviceStore = this.storeRegistry.getServiceStore(
          serviceStoreName as string
        );
        if (serviceStore?.serviceHasError) {
          serviceStoreHasError = [...serviceStoreHasError, serviceStoreName];
        }
        return serviceStoreHasError;
      },
      []
    );
  }

  get storeCount() {
    return Object.keys(this).filter((key: string) => key.includes("Store"))
      .length;
  }

  get isConnected() {
    return this.connectionStore?.serverConnectionStatus;
  }

  get currentChannelServices() {
    return this.channelServices.slice();
  }

  get developmentUriOverride() {
    return this.apiOptions?.uriOverride?.development || "";
  }
}

export default RootStore;
